import React from "react"
import { injectIntl } from "react-intl"
import moment from "moment"
import * as Yup from "yup"
import { makeStyles } from "@material-ui/core/styles"
import ArrowForwardIcon from "@material-ui/icons/ArrowForward"
import { Box, Card, Grid, Typography, Link } from "@material-ui/core"
import {
  FormikForm,
  FormikFormContainer,
  TextInputForm,
  DatePicker,
} from "@ridecell/web-components-material"
import { Field } from "formik"
import { Ridecell } from "@ridecell/web-sdk"

import useGlobalContext from "../../../utils/customHooks/useGlobalContext"
import LoadingButton from "../../LoadingButton"
import messages from "./messages"

const validationSchema = (formatMessage) =>
  Yup.object().shape({
    name: Yup.string()
      .required(formatMessage(messages.nameIsRequired))
      .nullable(),
    license_number: Yup.string()
      .required(formatMessage(messages.licenseIsRequired))
      .nullable(),
    license_expiry_date: Yup.date()
      .required(formatMessage(messages.expirationDateIsRequired))
      .nullable(),
    date_of_birth: Yup.date()
      .required(formatMessage(messages.dateOfBirthIsRequired))
      .nullable(),
    license_issued_state: Yup.string()
      .required(formatMessage(messages.provinceIsRequired))
      .nullable(),
  })

const useStyles = makeStyles({
  root: {
    width: "100%",
  },
  card: {
    margin: "24px",
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    boxShadow:
      "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px rgba(0, 0, 0, 0.14), 0px 1px 10px rgba(0, 0, 0, 0.12)",
    borderRadius: "4px",
  },
  agreementText: {
    fontSize: "12px",
    letterSpacing: "0.4px",
    color: "#616161",
  },
})

const DriverLicenseValidation = ({
  handleNext,
  intl: { formatMessage },
  createCustomer,
  ...props
}) => {
  const classes = useStyles()
  const {
    state: { loading, customer, tenantSettings },
    setLoading,
    setError,
  } = useGlobalContext()

  const saveLicense = async (values) => {
    setLoading(true)
    try {
      await Ridecell.saveDriverLicense({
        customerId: customer.id,
        data: values,
      })
      handleNext()
    } catch (e) {
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Grid item xs={12} md={12} lg={6}>
      <Card className={classes.card}>
        <Box display="flex" sx={{ mb: "16px" }} justifyContent="center">
          <Typography>
            {formatMessage(messages.pleaseVerifyYourInformation)}
          </Typography>
        </Box>
        <FormikFormContainer
          initialValues={{
            name: "",
            license_number: "",
            license_expiry_date: "",
            date_of_birth: "",
            license_issued_state: "",
          }}
          onSubmit={async (values) => {
            saveLicense(values)
          }}
          validationSchema={validationSchema(formatMessage)}
        >
          {({ isValid, values }) => (
            <FormikForm>
              <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                <TextInputForm
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={formatMessage(messages.name)}
                  name="name"
                  required
                  value={values.name}
                />
              </Box>
              <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                <TextInputForm
                  color="secondary"
                  InputLabelProps={{
                    shrink: true,
                  }}
                  label={formatMessage(messages.license)}
                  name="license_number"
                  required
                  value={values.license_number}
                />
              </Box>
              <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                <Field name="expiration_date">
                  {({ form }) => (
                    <DatePicker
                      format="yyy-MM-DD"
                      name={"license_expiry_date"}
                      label={formatMessage(messages.expirationDate)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      required
                      onChange={(value) => {
                        form.setFieldValue(
                          "license_expiry_date",
                          moment(value).format("YYYY-MM-DD")
                        )
                      }}
                      value={values.license_expiry_date || null}
                    />
                  )}
                </Field>
              </Box>
              <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                <Field name="date_of_birth">
                  {({ form }) => (
                    <DatePicker
                      format="yyy-MM-DD"
                      label={formatMessage(messages.dateOfBirth)}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      fullWidth
                      required
                      name={"date_of_birth"}
                      onChange={(value) => {
                        form.setFieldValue(
                          "date_of_birth",
                          moment(value).format("YYYY-MM-DD")
                        )
                      }}
                      value={values.date_of_birth || null}
                    />
                  )}
                </Field>
              </Box>
              <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                <TextInputForm
                  color="secondary"
                  label={formatMessage(messages.province)}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  name="license_issued_state"
                  variant="standard"
                  required
                  value={values.license_issued_state}
                />
              </Box>
              <Box display="flex" justifyContent="center" sx={{ mb: "16px" }}>
                <Box display="flex" justifyContentt="center">
                  <Typography className={classes.agreementText}>
                    {formatMessage(messages.authorizationMessage, {
                      customerName: tenantSettings.customerName,
                    })}

                    <Link
                      target="_blank"
                      href={tenantSettings.agreementPageUrl}
                    >
                      {formatMessage(messages.memberAgreement)}
                    </Link>
                  </Typography>
                </Box>
              </Box>
              <LoadingButton
                disabled={!isValid}
                text={formatMessage(messages.next)}
                variant="contained"
                loading={loading}
                fullWidth
                props={{
                  fullWidth: true,
                  type: "submit",
                  endIcon: <ArrowForwardIcon />,
                  size: "large",
                }}
              />
            </FormikForm>
          )}
        </FormikFormContainer>
      </Card>
    </Grid>
  )
}

export default injectIntl(DriverLicenseValidation)
